<template>
  <Loader v-if="loader" />

  <div class="rimborso-page">
    <div class="page-body">
      <header class="page-header">
        <div class="header-content">
          <h2 class="page-title">
            <i class="pi pi-money-bill" />
            Info Richiesta Rimborsi
          </h2>
          <p class="page-subtitle">
            {{ summaryLabel }}
          </p>
        </div>
      </header>

      <section class="fp">
        <div class="fp-body">
          <div class="fp-fields">
            <div class="fp-field fp-field--daterange">
              <label class="fp-label">Periodo richiesta</label>
              <div class="fp-daterange">
                <div class="fp-daterange-item">
                  <span class="fp-daterange-label">Da</span>
                  <Calendar
                    v-model="dataDa"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                    :maxDate="maxDate"
                    placeholder="Da"
                  />
                </div>
                <div class="fp-daterange-item">
                  <span class="fp-daterange-label">A</span>
                  <Calendar
                    v-model="dataA"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                    :maxDate="maxDate"
                    placeholder="A"
                  />
                </div>
              </div>
            </div>

            <div class="fp-field fp-field--search">
              <label class="fp-label">Numero fattura</label>
              <div class="p-inputgroup">
                <InputText
                  v-model="searchNumero"
                  placeholder="Cerca per numero…"
                  @keyup.enter="onSearch"
                />
              </div>
            </div>

            <div class="fp-field fp-field--stato">
              <label class="fp-label">Stato</label>
              <Dropdown
                v-model="filterStato"
                :options="statoFilterOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Tutti gli stati"
                class="w-full"
              />
            </div>
          </div>

          <div class="fp-actions">
            <Button
              label="Azzera filtri"
              icon="pi pi-refresh"
              class="p-button-outlined p-button-secondary p-button-sm"
              @click="resetFilters"
            />
            <Button
              label="Cerca"
              icon="pi pi-search"
              @click="onSearch"
            />
          </div>
        </div>
      </section>

      <section v-if="loadedItems && items.length === 0 && !tableLoading" class="empty-state">
        <i class="pi pi-inbox empty-state-icon" />
        <h4>Nessuna richiesta trovata</h4>
        <p>Modifica i filtri o amplia l'intervallo di date per visualizzare altri risultati.</p>
      </section>

      <section v-else-if="loadedItems" class="table-card">
        <DataTable
          :value="items"
          :lazy="true"
          :paginator="true"
          :rows="perPage"
          :totalRecords="totalRecords"
          :first="first"
          :loading="tableLoading"
          :rowsPerPageOptions="[10, 25, 50, 100]"
          :sortField="sortField"
          :sortOrder="sortOrder"
          class="rimborso-table"
          responsiveLayout="scroll"
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="{first}–{last} di {totalRecords}"
          @page="onPage"
          @sort="onSort"
        >
          <Column field="numero" header="Numero fattura" :sortable="false">
            <template #body="{ data }">
              <a
                v-if="data.numero"
                :href="`/ask/clienti/fatture/${data.numero}/dettagli`"
                target="_blank"
                class="fattura-link"
              >
                {{ data.numero }}
                <i class="pi pi-external-link fattura-link-icon" />
              </a>
              <span v-else class="text-muted">—</span>
            </template>
          </Column>
          <Column field="importo" header="Importo" sortable>
            <template #body="{ data }">
              <span class="amount-cell">{{ formatCurrency(data.importo) }}</span>
            </template>
          </Column>
          <Column field="note" header="Note" :sortable="false">
            <template #body="{ data }">
              <span v-if="data.note" class="note-cell">{{ data.note }}</span>
              <span v-else class="text-muted">—</span>
            </template>
          </Column>
          <Column field="data" header="Data richiesta" sortable>
            <template #body="{ data }">
              <span class="date-cell">{{ formatDate(data.data) }}</span>
            </template>
          </Column>
          <Column field="data_confirmed_rimborso" header="Data conferma" sortable>
            <template #body="{ data }">
              <span class="date-cell">{{ formatDate(data.data_confirmed_rimborso) }}</span>
            </template>
          </Column>
          <Column field="stato" header="Stato" :sortable="false">
            <template #body="{ data }">
              <span :class="['stato-badge', getStatoClass(data)]">
                {{ getStatoLabel(data) }}
              </span>
            </template>
          </Column>
          <Column field="user" header="Utente" :sortable="false">
            <template #body="{ data }">
              <span class="user-cell">{{ data.user || data.username || data.user_id || '—' }}</span>
            </template>
          </Column>
          <Column header="Azioni" :exportable="false" headerStyle="width: 8rem">
            <template #body="{ data }">
              <Button
                v-if="!isRimborsoEmesso(data)"
                label="Conferma"
                icon="pi pi-check"
                class="p-button-success p-button-sm p-button-outlined action-btn"
                @click="onConfirmClick(data)"
              />
              <span v-else class="text-muted">—</span>
            </template>
          </Column>
          <Column header="Ricevuta" :exportable="false" headerStyle="width: 9rem">
            <template #body="{ data }">
              <div class="ricevuta-actions">
                <template v-if="Number(data.has_ricevuta)">
                  <Button
                    icon="pi pi-download"
                    class="p-button-sm p-button-outlined p-button-secondary action-btn"
                    :loading="downloadingRicevutaId === data.id"
                    :disabled="uploadingRicevutaId === data.id"
                    v-tooltip.top="'Scarica ricevuta'"
                    @click="onDownloadRicevuta(data)"
                  />
                  <Button
                    icon="pi pi-refresh"
                    class="p-button-sm p-button-outlined p-button-secondary action-btn"
                    :loading="uploadingRicevutaId === data.id"
                    :disabled="uploadingRicevutaId !== null"
                    v-tooltip.top="'Sostituisci ricevuta'"
                    @click="onReplaceRicevutaClick(data)"
                  />
                </template>
                <Button
                  v-else
                  :label="uploadingRicevutaId === data.id ? `${uploadProgress}%` : 'Carica'"
                  icon="pi pi-upload"
                  class="p-button-sm p-button-outlined action-btn"
                  :loading="uploadingRicevutaId === data.id"
                  :disabled="uploadingRicevutaId !== null"
                  v-tooltip.top="'Carica la ricevuta del rimborso'"
                  @click="pickRicevuta(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </section>
    </div>

    <!-- un solo selettore di file per tutte le righe: la riga è in ricevutaRimborso -->
    <input
      ref="ricevutaInput"
      type="file"
      class="ricevuta-file-input"
      :accept="acceptRicevuta"
      @change="onRicevutaSelected"
    />

    <Dialog
      v-model:visible="replaceRicevutaDialog"
      header="Sostituisci ricevuta"
      :modal="true"
      :style="{ width: '480px' }"
      class="confirm-dialog"
    >
      <div class="confirm-content">
        <i class="pi pi-exclamation-triangle confirm-icon" />
        <div>
          <p class="confirm-message">Vuoi sostituire la ricevuta?</p>
          <p v-if="ricevutaRimborso" class="confirm-details">
            Fattura <strong>{{ ricevutaRimborso.numero }}</strong>: la ricevuta attuale non sarà più collegata al rimborso.
          </p>
        </div>
      </div>
      <template #footer>
        <Button
          label="No"
          icon="pi pi-times"
          class="p-button-text"
          @click="replaceRicevutaDialog = false"
        />
        <Button
          label="Sì, scegli il file"
          icon="pi pi-upload"
          @click="confirmReplaceRicevuta"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="confirmDialog"
      header="Conferma rimborso"
      :modal="true"
      :style="{ width: '480px' }"
      class="confirm-dialog"
    >
      <div class="confirm-content">
        <i class="pi pi-exclamation-triangle confirm-icon" />
        <div>
          <p class="confirm-message">Vuoi confermare il rimborso?</p>
          <p v-if="selectedRimborso" class="confirm-details">
            Fattura <strong>{{ selectedRimborso.numero }}</strong>
            · {{ formatCurrency(selectedRimborso.importo) }}
          </p>
        </div>
      </div>
      <template #footer>
        <Button
          label="No"
          icon="pi pi-times"
          class="p-button-text"
          @click="closeConfirmDialog"
        />
        <Button
          label="Sì, conferma"
          icon="pi pi-check"
          class="p-button-success"
          @click="confirmRimborso"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import Loader from '@/components/Loader.vue'
import Service from '@/services/ask-pratiche/pratiche-service'
import moment from 'moment'
import Button from 'primevue/button'
import Calendar from 'primevue/calendar'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import { ref, computed, onMounted, getCurrentInstance } from 'vue'

export default {
  name: 'InfoRichiestaRimborsi',
  components: {
    Loader,
    Button,
    Calendar,
    Dropdown,
    InputText,
  },

  setup() {
    // stessi limiti del backend (RimborsoRicevutaController di ask_pratiche)
    const MAX_MB_RICEVUTA = 50
    const ESTENSIONI_RICEVUTA = [
      'pdf', 'p7m', 'xml',
      'jpg', 'jpeg', 'png', 'gif', 'tif', 'tiff', 'heic',
      'doc', 'docx', 'odt', 'rtf', 'txt',
      'xls', 'xlsx', 'ods', 'csv',
      'eml', 'msg',
      'zip', 'rar', '7z',
    ]
    const acceptRicevuta = ESTENSIONI_RICEVUTA.map((ext) => `.${ext}`).join(',')

    const loader = ref(false)
    const tableLoading = ref(false)
    const loadedItems = ref(false)
    const items = ref([])
    const today = moment().startOf('day').toDate()
    const dataDa = ref(today)
    const dataA = ref(moment().startOf('day').toDate())
    const searchNumero = ref('')
    const filterStato = ref('tutti')
    const statoFilterOptions = [
      { label: 'Tutti', value: 'tutti' },
      { label: 'In attesa', value: 'in_attesa' },
      { label: 'Emesse', value: 'emesse' },
    ]
    const maxDate = ref(new Date())
    const perPage = ref(50)
    const totalRecords = ref(0)
    const first = ref(0)
    const currentPage = ref(1)
    const confirmDialog = ref(false)
    const selectedRimborso = ref(null)
    const sortField = ref('data')
    const sortOrder = ref(-1)
    const downloadingRicevutaId = ref(null)
    const uploadingRicevutaId = ref(null)
    const uploadProgress = ref(0)
    const ricevutaInput = ref(null)
    const ricevutaRimborso = ref(null)
    const replaceRicevutaDialog = ref(false)

    const self = getCurrentInstance()
    self.$http = self.appContext.config.globalProperties.$http
    self.$eventBus = self.appContext.config.globalProperties.$eventBus
    const service = new Service(self.$http, self.$eventBus)

    const currencyFormatter = new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    })

    const summaryLabel = computed(() => {
      if (!loadedItems.value && loader.value) {
        return 'Caricamento richieste rimborso…'
      }

      if (totalRecords.value === 1) {
        return '1 richiesta trovata'
      }

      return `${totalRecords.value} richieste trovate`
    })

    const formatDate = (value) => {
      if (!value) {
        return '—'
      }

      const m = moment(value)
      return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : value
    }

    const formatCurrency = (value) => {
      const amount = parseFloat(value ?? 0)
      return currencyFormatter.format(Number.isNaN(amount) ? 0 : amount)
    }

    const isRimborsoEmesso = (row) => {
      if (row?.stato === 'emessa') {
        return true
      }

      return Number(row?.used) === 1 ||
        (row?.data_confirmed_rimborso && moment(row.data_confirmed_rimborso).isValid())
    }

    const getStatoLabel = (row) => {
      if (row?.stato === 'emessa') {
        return 'Emesso'
      }

      if (row?.stato === 'in_attesa') {
        return 'In attesa'
      }

      return isRimborsoEmesso(row) ? 'Emesso' : 'In attesa'
    }

    const getStatoClass = (row) => {
      return isRimborsoEmesso(row) ? 'stato-badge--emesso' : 'stato-badge--attesa'
    }

    const formatDateParam = (value) => {
      if (!value) {
        return null
      }

      return moment(value).format('YYYY-MM-DD')
    }

    const getSortValue = (row, field) => {
      const value = row?.[field]

      if (field === 'importo') {
        const amount = parseFloat(value ?? 0)
        return Number.isNaN(amount) ? 0 : amount
      }

      if (field === 'data' || field === 'data_confirmed_rimborso') {
        if (!value) {
          return null
        }

        const timestamp = moment(value).valueOf()
        return Number.isNaN(timestamp) ? null : timestamp
      }

      return value
    }

    const sortItems = (list) => {
      const field = sortField.value
      const order = sortOrder.value

      if (!field || !order) {
        return list
      }

      return [...list].sort((a, b) => {
        const aVal = getSortValue(a, field)
        const bVal = getSortValue(b, field)

        if (aVal === null && bVal === null) {
          return 0
        }
        if (aVal === null) {
          return 1
        }
        if (bVal === null) {
          return -1
        }
        if (aVal === bVal) {
          return 0
        }

        return (aVal < bVal ? -1 : 1) * order
      })
    }

    const buildParams = (page = currentPage.value) => {
      const params = {
        page,
        per_page: perPage.value,
      }

      const from = formatDateParam(dataDa.value)
      const to = formatDateParam(dataA.value)
      const numero = searchNumero.value.trim()

      if (from) {
        params.data_da = from
      }
      if (to) {
        params.data_a = to
      }
      if (numero) {
        params.numero = numero
      }
      if (filterStato.value) {
        params.stato = filterStato.value
      }

      return params
    }

    const getData = async (page = 1, showFullLoader = false) => {
      if (showFullLoader) {
        loader.value = true
      } else {
        tableLoading.value = true
      }

      try {
        const response = await service.getInfoRichiesteRimborso(buildParams(page))
        items.value = sortItems(response.items ?? [])

        const pagination = response.pagination ?? {}
        currentPage.value = pagination.current_page ?? page
        perPage.value = pagination.per_page ?? perPage.value
        totalRecords.value = pagination.total ?? items.value.length
        first.value = (currentPage.value - 1) * perPage.value
        loadedItems.value = true
      } catch (error) {
        console.error('Errore nel caricamento delle richieste rimborso:', error)
        self.proxy.$toast?.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.response?.data?.message || 'Errore durante il caricamento delle richieste rimborso',
          life: 4000,
        })
      } finally {
        loader.value = false
        tableLoading.value = false
      }
    }

    const resetFilters = () => {
      const todayDate = moment().startOf('day').toDate()
      dataDa.value = todayDate
      dataA.value = moment().startOf('day').toDate()
      searchNumero.value = ''
      filterStato.value = 'tutti'
      onSearch()
    }

    const onSearch = () => {
      currentPage.value = 1
      first.value = 0
      getData(1, true)
    }

    const onPage = (event) => {
      const page = event.page + 1
      perPage.value = event.rows
      first.value = event.first
      currentPage.value = page
      getData(page)
    }

    const onSort = (event) => {
      sortField.value = event.sortField
      sortOrder.value = event.sortOrder
      items.value = sortItems(items.value)
    }

    const onConfirmClick = (row) => {
      selectedRimborso.value = row
      confirmDialog.value = true
    }

    const closeConfirmDialog = () => {
      confirmDialog.value = false
      selectedRimborso.value = null
    }

    const onDownloadRicevuta = async (row) => {
      downloadingRicevutaId.value = row.id

      try {
        await service.downloadRicevutaRimborso({ id: row.id, numero: row.numero })
      } catch (error) {
        console.error('Errore durante il download della ricevuta:', error)
        self.proxy.$toast?.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.message || error?.response?.data?.message || 'Ricevuta non trovata',
          life: 4000,
        })
      } finally {
        downloadingRicevutaId.value = null
      }
    }

    const pickRicevuta = (row) => {
      ricevutaRimborso.value = row
      if (ricevutaInput.value) {
        ricevutaInput.value.value = ''
        ricevutaInput.value.click()
      }
    }

    const onReplaceRicevutaClick = (row) => {
      ricevutaRimborso.value = row
      replaceRicevutaDialog.value = true
    }

    const confirmReplaceRicevuta = () => {
      replaceRicevutaDialog.value = false
      pickRicevuta(ricevutaRimborso.value)
    }

    const onRicevutaSelected = async (event) => {
      const file = event?.target?.files?.[0]
      const row = ricevutaRimborso.value
      if (!file || !row) {
        return
      }

      const ext = String(file.name.split('.').pop() || '').toLowerCase()
      if (!ESTENSIONI_RICEVUTA.includes(ext)) {
        self.proxy.$toast?.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: `Formato non consentito. Formati ammessi: ${ESTENSIONI_RICEVUTA.join(', ')}`,
          life: 6000,
        })
        return
      }
      if (file.size > MAX_MB_RICEVUTA * 1024 * 1024) {
        self.proxy.$toast?.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: `Il file supera i ${MAX_MB_RICEVUTA} MB`,
          life: 4000,
        })
        return
      }

      uploadingRicevutaId.value = row.id
      uploadProgress.value = 0

      try {
        const res = await service.uploadRicevutaRimborso(row.id, file, (progress) => { uploadProgress.value = progress })
        row.has_ricevuta = 1

        self.proxy.$toast?.add({
          severity: 'success',
          summary: 'Successo',
          detail: res?.message || 'Ricevuta caricata',
          life: 3000,
        })
      } catch (error) {
        console.error('Errore nel caricamento della ricevuta:', error)
        self.proxy.$toast?.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.response?.status === 413 && !error?.response?.data?.message
            ? 'Il file supera il limite del server per un singolo invio'
            : (error?.response?.data?.message || 'Errore durante il caricamento della ricevuta'),
          life: 6000,
        })
      } finally {
        uploadingRicevutaId.value = null
        ricevutaRimborso.value = null
      }
    }

    const confirmRimborso = async () => {
      if (!selectedRimborso.value?.id || !selectedRimborso.value?.numero) {
        self.proxy.$toast?.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: 'Dati rimborso non validi',
          life: 4000,
        })
        return
      }

      loader.value = true

      try {
        await service.confirmRichiestaRimborso({
          id: selectedRimborso.value.id,
          numero: selectedRimborso.value.numero,
        })

        self.proxy.$toast?.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Richiesta rimborso confermata',
          life: 3000,
        })

        closeConfirmDialog()
        await getData(currentPage.value)
      } catch (error) {
        console.error('Errore durante la conferma del rimborso:', error)
        self.proxy.$toast?.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.response?.data?.message || 'Errore durante la conferma del rimborso',
          life: 4000,
        })
      } finally {
        loader.value = false
      }
    }

    onMounted(() => {
      getData(1, true)
    })

    return {
      loader,
      tableLoading,
      loadedItems,
      items,
      dataDa,
      dataA,
      searchNumero,
      filterStato,
      statoFilterOptions,
      maxDate,
      perPage,
      totalRecords,
      first,
      sortField,
      sortOrder,
      downloadingRicevutaId,
      uploadingRicevutaId,
      uploadProgress,
      ricevutaInput,
      ricevutaRimborso,
      replaceRicevutaDialog,
      acceptRicevuta,
      selectedRimborso,
      summaryLabel,
      formatDate,
      formatCurrency,
      isRimborsoEmesso,
      getStatoLabel,
      getStatoClass,
      getData,
      resetFilters,
      onSearch,
      onPage,
      onSort,
      confirmDialog,
      onConfirmClick,
      closeConfirmDialog,
      confirmRimborso,
      onDownloadRicevuta,
      pickRicevuta,
      onReplaceRicevutaClick,
      confirmReplaceRicevuta,
      onRicevutaSelected,
    }
  },
}
</script>

<style scoped>
.rimborso-page {
  width: 100%;
  max-width: 100%;
}

.page-header {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-700, var(--primary-color)) 100%);
  border-radius: 4px;
  color: #fff;
  margin-bottom: 1.5rem;
  padding: 1.5rem 1.75rem;
}

.page-title {
  align-items: center;
  display: flex;
  font-size: 1.65rem;
  font-weight: 600;
  gap: 0.65rem;
  letter-spacing: -0.02em;
  margin: 0;
}

.page-subtitle {
  font-size: 0.92rem;
  margin: 0.45rem 0 0;
  opacity: 0.92;
}

.page-body {
  padding: 1.75rem 2.5rem 2.5rem;
}

.fp {
  border: 1px solid var(--surface-border);
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.05);
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.fp-body {
  background: var(--surface-ground);
}

.fp-fields {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem 1.1rem;
  padding: 1rem 1.1rem 0.85rem;
}

.fp-field {
  display: flex;
  flex: 1 1 180px;
  flex-direction: column;
  min-width: 150px;
}

.fp-field--daterange {
  flex: 2 1 320px;
  min-width: 260px;
}

.fp-field--search {
  flex: 1.5 1 220px;
}

.fp-field--stato {
  flex: 1 1 180px;
  max-width: 220px;
}

.fp-label {
  color: var(--text-color-secondary);
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  margin-bottom: 0.4rem;
  text-transform: uppercase;
}

.fp-daterange {
  align-items: flex-end;
  display: flex;
  gap: 0.4rem;
}

.fp-daterange-item {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}

.fp-daterange-label {
  color: var(--text-color-secondary);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.fp-daterange-item :deep(.p-calendar) {
  width: 100%;
}

.fp-daterange-sep {
  color: var(--text-color-secondary);
  flex-shrink: 0;
  font-size: 0.85rem;
  opacity: 0.45;
}

.fp-actions {
  align-items: center;
  background: var(--surface-card);
  border-top: 1px solid var(--surface-border);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.65rem 1.1rem;
}

.table-card {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.05);
  overflow: hidden;
}

.rimborso-table :deep(.p-datatable-header) {
  background: var(--surface-card);
  border: none;
}

.rimborso-table :deep(.p-datatable-thead > tr > th) {
  background: var(--surface-ground);
  border-bottom: 1px solid var(--surface-border);
  color: var(--text-color-secondary);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0.85rem 1rem;
  text-transform: uppercase;
}

.rimborso-table :deep(.p-datatable-tbody > tr > td) {
  padding: 0.85rem 1rem;
  vertical-align: middle;
}

.rimborso-table :deep(.p-datatable-tbody > tr:hover) {
  background: var(--surface-hover);
}

.rimborso-table :deep(.p-paginator) {
  background: var(--surface-ground);
  border-top: 1px solid var(--surface-border);
  padding: 0.75rem 1rem;
}

.fattura-link {
  align-items: center;
  color: var(--primary-color);
  display: inline-flex;
  font-weight: 600;
  gap: 0.35rem;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.fattura-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.fattura-link-icon {
  font-size: 0.72rem;
  opacity: 0.7;
}

.amount-cell {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

/* la nota si legge tutta: va a capo dentro una colonna di larghezza fissa */
.note-cell {
  display: block;
  max-width: 320px;
  min-width: 220px;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.ricevuta-actions {
  display: flex;
  gap: 0.4rem;
}

.ricevuta-file-input {
  display: none;
}

.date-cell,
.user-cell {
  color: var(--text-color);
  font-size: 0.92rem;
}

.text-muted {
  color: var(--text-color-secondary);
}

.stato-badge {
  border-radius: 999px;
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 0.28rem 0.7rem;
}

.stato-badge--emesso {
  background: rgba(22, 163, 74, 0.12);
  color: #15803d;
}

.stato-badge--attesa {
  background: rgba(217, 119, 6, 0.12);
  color: #b45309;
}

.action-btn {
  transition: transform 0.15s ease;
}

.action-btn:active {
  transform: scale(0.97);
}

.empty-state {
  align-items: center;
  background: var(--surface-card);
  border: 1px dashed var(--surface-border);
  display: flex;
  flex-direction: column;
  padding: 3.5rem 1.5rem;
  text-align: center;
}

.empty-state-icon {
  color: var(--text-color-secondary);
  font-size: 2.5rem;
  margin-bottom: 1rem;
  opacity: 0.45;
}

.empty-state h4 {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
}

.empty-state p {
  color: var(--text-color-secondary);
  margin: 0;
  max-width: 36ch;
}

.confirm-content {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  padding: 0.25rem 0;
}

.confirm-icon {
  color: var(--orange-500, #f97316);
  flex-shrink: 0;
  font-size: 2rem;
}

.confirm-message {
  font-weight: 600;
  margin: 0 0 0.35rem;
}

.confirm-details {
  color: var(--text-color-secondary);
  font-size: 0.92rem;
  margin: 0;
}

:deep(.confirm-dialog .p-dialog-header) {
  border-bottom: 1px solid var(--surface-border);
  padding: 1.1rem 1.35rem;
}

:deep(.confirm-dialog .p-dialog-content) {
  padding: 1.25rem 1.35rem;
}

:deep(.confirm-dialog .p-dialog-footer) {
  border-top: 1px solid var(--surface-border);
  padding: 0.85rem 1.35rem;
}

@media (max-width: 768px) {
  .page-body {
    padding: 1rem;
  }

  .page-header {
    padding: 1.25rem 1rem;
  }

  .fp-fields {
    flex-direction: column;
  }

  .fp-field,
  .fp-field--daterange,
  .fp-field--search,
  .fp-field--stato {
    flex: 1 1 100%;
    max-width: none;
    min-width: 0;
    width: 100%;
  }

  .fp-actions {
    flex-wrap: wrap;
    justify-content: stretch;
  }

  .fp-actions :deep(.p-button) {
    flex: 1;
  }
}
</style>
